import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  type ForwardedRef,
} from "react";
import { App } from "antd";
import type {
  Member,
  MemberSelectorFilters,
  MemberSelectorProps,
  MemberSelectorRef,
} from "../types";
import {
  ALL_TEAMS_ID,
  CONFIRM_TEXTS,
  SEARCH_DEBOUNCE_MS,
} from "../utils/constants";

type UseMemberSelectorStateParams = Pick<
  MemberSelectorProps,
  | "visible"
  | "defaultValue"
  | "mode"
  | "destroyOnHidden"
  | "defaultFilters"
> & {
  ref: ForwardedRef<MemberSelectorRef>;
};

const EMPTY_FILTERS: MemberSelectorFilters = {};

export function useMemberSelectorState({
  visible,
  defaultValue,
  mode,
  destroyOnHidden = true,
  defaultFilters,
  ref,
}: UseMemberSelectorStateParams) {
  const { modal } = App.useApp();

  const [shouldRender, setShouldRender] = useState(visible);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(
    ALL_TEAMS_ID
  );
  const [searchKeyword, setSearchKeyword] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [filters, setFilters] = useState<MemberSelectorFilters>(
    defaultFilters ?? EMPTY_FILTERS
  );
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<number>>(
    new Set()
  );
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);

  const selectedMembersRef = useRef<Member[]>([]);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applySelection = useCallback((members: Member[]) => {
    selectedMembersRef.current = members;
    setSelectedMembers(members);
    setSelectedMemberIds(new Set(members.map((member) => member.uid)));
  }, []);

  const restoreSelectionFromDefaultValue = useCallback(() => {
    if (!defaultValue) {
      applySelection([]);
      return;
    }

    const membersArray = Array.isArray(defaultValue)
      ? defaultValue
      : [defaultValue];

    applySelection(membersArray);
  }, [defaultValue, applySelection]);

  const resetSelection = useCallback(() => {
    applySelection([]);
  }, [applySelection]);

  const getSelectedMembers = useCallback(() => {
    return [...selectedMembersRef.current];
  }, []);

  const resetFilterState = useCallback(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }

    setSelectedTeamId(ALL_TEAMS_ID);
    setSearchKeyword("");
    setInputValue("");
    setFilters(defaultFilters ?? EMPTY_FILTERS);
  }, [defaultFilters]);

  useImperativeHandle(
    ref,
    () => ({
      reset: resetSelection,
      getSelectedMembers,
    }),
    [getSelectedMembers, resetSelection]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    restoreSelectionFromDefaultValue();
    resetFilterState();
  }, [isOpen, resetFilterState, restoreSelectionFromDefaultValue]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (visible) {
      setShouldRender(true);
    }
  }, [visible]);

  const handleAfterOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);

      if (!open && destroyOnHidden) {
        setShouldRender(false);
      }

      if (!open) {
        restoreSelectionFromDefaultValue();
      }
    },
    [destroyOnHidden, restoreSelectionFromDefaultValue]
  );

  const handleSearch = useCallback((keyword: string) => {
    setInputValue(keyword);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (keyword.trim() === "") {
      setSearchKeyword("");
      return;
    }

    searchTimerRef.current = setTimeout(() => {
      setSearchKeyword(keyword);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const handleTeamSelect = useCallback((teamId: number | null) => {
    setSelectedTeamId(teamId);
  }, []);

  const handleFilterChange = useCallback(
    <Key extends keyof MemberSelectorFilters>(
      key: Key,
      value: MemberSelectorFilters[Key]
    ) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value || undefined,
      }));
    },
    []
  );

  const handleMemberSelect = useCallback(
    (member: Member) => {
      if (mode === "single") {
        applySelection([member]);
        return;
      }

      applySelection([...selectedMembersRef.current, member]);
    },
    [mode, applySelection]
  );

  const handleMemberDeselect = useCallback(
    (memberId: number) => {
      applySelection(
        selectedMembersRef.current.filter((member) => member.uid !== memberId)
      );
    },
    [applySelection]
  );

  const handleClearAll = useCallback(() => {
    modal.confirm({
      title: CONFIRM_TEXTS.CLEAR_ALL,
      onOk: () => {
        applySelection([]);
      },
    });
  }, [modal, applySelection]);

  return {
    shouldRender,
    isOpen,
    selectedTeamId,
    inputValue,
    searchKeyword,
    filters,
    selectedMembers,
    selectedMemberIds,
    canConfirm: selectedMembers.length > 0,
    handleAfterOpenChange,
    handleSearch,
    handleTeamSelect,
    handleFilterChange,
    handleResetFilters: resetFilterState,
    handleMemberSelect,
    handleMemberDeselect,
    handleClearAll,
  };
}
